<template>
    <div
        class="grid grid-cols-2 divide-x divide-neutral-200 dark:divide-neutral-700 border-b border-neutral-200 dark:border-neutral-700"
    >
        <div class="[&_.cm-editor]:h-screen">
            <AdminHTML v-model="modelValue" v-bind="$attrs" />
        </div>
        <div>
            <div class="sticky top-0 w-full h-screen">
                <div class="relative min-h-screen">
                    <iframe v-if="url" class="w-full h-screen" :src="url" frameborder="0" allowfullscreen></iframe>

                    <!-- Show error -->
                    <div v-if="error" class="absolute inset-0 text-red-600 break-all bg-red-100">
                        <div class="px-4 py-2 text-sm">
                            {{ error }}
                        </div>
                    </div>

                    <!-- Show stats -->
                    <div
                        class="absolute inset-x-0 bottom-0 z-50 flex items-center justify-between w-full text-xs text-white bg-neutral-900"
                    >
                        <div v-if="stats.duration" class="px-2 py-1 text-center">
                            {{
                                lang('fields::fields.html-live-doc.stats.duration', { time: stats.duration.toFixed(3) })
                            }}
                        </div>
                        <div v-if="stats.status" class="px-2 py-1 text-center">
                            {{ lang(`fields::fields.html-live-doc.status.${stats.status}`) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { watch, ref, Ref } from 'vue';
import type { FieldComponent } from '@src/Common/Components/types';
import { useToast } from '@src/Common/Composables/useToast';

const modelValue = defineModel({
    type: String,
});

const props = withDefaults(defineProps<FieldComponent>(), {
    source: '',
    params: {
        paper: 'a4',
        orientation: 'portrait',
        variables: [],
    },
});

const url: Ref<string> = ref<string>('');

const stats = ref({
    duration: null,
    status: null,
});

const error = ref<string | null>(null);

const generate = (): void => {
    stats.value.status = 'generating';
    axios
        .post(props.source, {
            ...props.params,
            content: modelValue.value,
        })
        .then(({ data }) => {
            if (data.success) {
                url.value = data.url;
                stats.value.duration = data.duration;
                error.value = null;
                stats.value.status = 'success';
            } else {
                error.value = data.message;
                useToast({
                    message: data.message,
                    type: 'error',
                });
                stats.value.status = 'error';
            }
        })
        .catch((error) => {
            console.error(error);
        });
};

let generationTimeout = null;

const deferredGeneration = () => {
    if (generationTimeout) {
        clearTimeout(generationTimeout);
    }

    generationTimeout = setTimeout(() => {
        generate();
    }, 300);
};

watch(
    () => modelValue.value,
    () => {
        deferredGeneration();
    },
    {
        immediate: true,
    },
);

watch(
    () => props.params,
    () => {
        deferredGeneration();
    },
    {
        immediate: true,
        deep: true,
    },
);
</script>
